import Swal from "sweetalert2";
import { dialogo, escapar, mostrarEspera, mostrarError, nota, notas } from "./dialogos_cartera";

// Ventanas comunes de los listados (encomiendas, visitantes...): menú de opciones con
// tarjetas grandes y asistente de carga masiva desde Excel. Mismo estilo que la cartera.

export { escapar, nota, notas };

export const formatearFecha = (iso) => iso.split("-").reverse().join("/");

// Rango "07/10/2026" o "01/10/2026 a 07/10/2026" de una lista de fechas "aaaa-mm-dd".
export const rangoFechas = (fechas) => {
  const orden = [...fechas].sort();
  if (!orden.length) return "";
  const [primera, ultima] = [orden[0], orden[orden.length - 1]];
  return primera === ultima ? formatearFecha(primera) : `${formatearFecha(primera)} a ${formatearFecha(ultima)}`;
};

export const conteoPor = (registros, campo) =>
  Object.entries(registros.reduce((acc, r) => ({ ...acc, [r[campo]]: (acc[r[campo]] || 0) + 1 }), {}))
    .map(([valor, n]) => `${escapar(valor)}: ${n}`).join(" · ");

export const filaResumen = (icono, etiqueta, valor) => `
  <div class="cartera-fila">
    <span class="cartera-fila-icono"><i class="bi ${icono}"></i></span>
    <span class="cartera-fila-etiqueta">${etiqueta}</span>
    <span class="cartera-fila-valor">${valor}</span>
  </div>`;

// Resumen a la izquierda y "Así quedarán las direcciones" a la derecha (si hay direcciones).
export const columnasRevision = (filasResumen, ejemplos) => !ejemplos.length ? `
  <div class="cartera-lista">${filasResumen.join("")}</div>` : `
  <div class="cartera-columnas">
    <div>
      <p class="cartera-subtitulo">Resumen del archivo</p>
      <div class="cartera-lista">${filasResumen.join("")}</div>
    </div>
    <div>
      <p class="cartera-subtitulo">Así quedarán las direcciones</p>
      <div class="cartera-lista">${ejemplos.map(e => `
        <div class="cartera-conversion">
          <span class="cartera-conversion-original">${escapar(e.original)}</span>
          <i class="bi bi-arrow-right cartera-conversion-flecha"></i>
          <span class="cartera-conversion-final">${escapar(e.direccion)}</span>
        </div>`).join("")}</div>
    </div>
  </div>`;

// Ventana con tarjetas grandes; cada tarjeta es una acción. Devuelve la clave elegida o null si cierra.
// Una opción con bloqueada: true se ve en gris con candado y no se puede elegir.
export const menuOpciones = async ({ icono, titulo, texto, opciones }) => {
  const tarjetas = opciones.map(o => `
    <button type="button" class="cartera-opcion${o.bloqueada ? " bloqueada" : ""}" data-clave="${o.clave}"${o.bloqueada ? " disabled" : ""}>
      <span class="cartera-opcion-icono"><i class="bi ${o.icono}"></i></span>
      <span class="cartera-opcion-textos">
        <span class="cartera-opcion-nombre">${o.nombre}</span>
        <span class="cartera-opcion-detalle">${o.detalle}</span>
      </span>
      <i class="bi ${o.bloqueada ? "bi-lock-fill" : "bi-chevron-right"} cartera-opcion-check"></i>
    </button>`).join("");

  let elegida = null;
  await dialogo({
    icono,
    tono: "rojo",
    titulo,
    texto,
    contenido: `<div class="cartera-opciones">${tarjetas}</div>`,
    showConfirmButton: false,
    cancelar: "Cerrar",
    cerrar: true,
    didOpen: (popup) => {
      popup.querySelectorAll(".cartera-opcion:not(.bloqueada)").forEach(boton => {
        boton.addEventListener("click", () => {
          elegida = boton.dataset.clave;
          Swal.close();
        });
      });
    },
  });
  return elegida;
};

// Menú del botón de archivos de un listado: exportar y, si puede cargar, plantilla y carga masiva.
// plural va en "Cargar encomiendas"; los detalles son el texto pequeño de cada tarjeta.
export const menuArchivos = ({ titulo, plural, detalleExportar, detallePlantilla, detalleCargar, puedeCargar }) => menuOpciones({
  icono: "bi-file-earmark-spreadsheet",
  titulo,
  texto: "¿Qué desea hacer?",
  opciones: [
    { clave: "exportar", icono: "bi-download", nombre: "Exportar listado",
      detalle: detalleExportar },
    ...(puedeCargar ? [
      { clave: "plantilla", icono: "bi-file-earmark-excel", nombre: "Descargar plantilla", detalle: detallePlantilla },
      { clave: "cargar", icono: "bi-upload", nombre: `Cargar ${plural}`, detalle: detalleCargar },
    ] : []),
  ],
});

const abrirExplorador = () => new Promise(resolve => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".xlsx,.xls,.xlsm,.ods,.csv,.txt";
  input.onchange = () => resolve(input.files[0] || null);
  input.oncancel = () => resolve(null);
  input.click();
});

const problemaConArchivo = async (titulo, texto, contenido = "") => {
  const r = await dialogo({
    icono: "bi-exclamation-circle", tono: "naranja", titulo, texto, contenido,
    confirmar: "Elegir otro archivo", iconoConfirmar: "bi-folder2-open",
    cancelar: "Cerrar",
  });
  return r.isConfirmed;
};

const listaErrores = (errores) => {
  const visibles = errores.slice(0, 5).map(e => filaResumen("bi-x-circle", `Fila ${e.fila}`, escapar(e.texto))).join("");
  const resto = errores.length > 5 ? `<p class="cartera-texto" style="margin-top:8px;font-size:14px;">Y ${errores.length - 5} filas más con problemas.</p>` : "";
  return `<div class="cartera-lista">${visibles}</div>${resto}`;
};

// Asistente de 2 pasos: elegir archivo (o descargar la plantilla) y revisar antes de cargar.
// t: textos del módulo:
//   icono, titulo, texto, notaColumnas, faltanColumnas (HTML), filaVacia,
//   uno ("encomienda"), varios ("encomiendas"), ninguno ("ninguna encomienda"), titulo exito ("Encomiendas cargadas").
// leer(archivo) → { encontroEncabezado, registros, errores }; revision(nombreArchivo, res) → HTML;
// guardar(registros) → { exito, mensaje }. Devuelve true si se guardó.
export const asistenteCargaMasiva = async ({ t, descargarPlantilla, leer, revision, guardar }) => {
  const cantidad = (n) => `${n} ${n === 1 ? t.uno : t.varios}`;
  while (true) {
    const elegido = await dialogo({
      icono: t.icono,
      paso: "Paso 1 de 2",
      titulo: t.titulo,
      texto: t.texto,
      contenido: notas(
        nota("info", "bi-file-earmark-excel",
          "¿No tiene el archivo? Toque <strong>Descargar plantilla</strong>. Trae ejemplos de cómo llenar cada columna y una hoja con instrucciones."),
        nota("info", "bi-list-check", t.notaColumnas)),
      amplio: true,
      confirmar: "Buscar archivo",
      iconoConfirmar: "bi-folder2-open",
      showDenyButton: true,
      denyButtonText: `<i class="bi bi-download"></i><span>Descargar plantilla</span>`,
      cancelar: "Cancelar",
      cerrar: true,
    });
    if (elegido.isDenied) {
      descargarPlantilla();
      continue;
    }
    if (!elegido.isConfirmed) return false;
    const archivo = await abrirExplorador();
    if (!archivo) continue;

    mostrarEspera("Leyendo el archivo", `Estamos revisando "${escapar(archivo.name)}". Esto toma unos segundos.`);
    let res;
    try {
      res = await leer(archivo);
    } catch (error) {
      if (await problemaConArchivo("No se pudo abrir el archivo",
        `Revise que "${escapar(archivo.name)}" sea un Excel o un CSV y que no esté dañado. Detalle: ${escapar(error.message)}`)) continue;
      return false;
    }
    if (!res.encontroEncabezado) {
      if (await problemaConArchivo("No encontramos las columnas",
        `En "${escapar(archivo.name)}" falta la fila de títulos con ${t.faltanColumnas}. Lo más fácil es descargar la plantilla y copiar ahí los datos.`)) continue;
      return false;
    }
    if (res.errores.length > 0) {
      if (await problemaConArchivo(
        res.errores.length === 1 ? "Hay una fila por corregir" : `Hay ${res.errores.length} filas por corregir`,
        `Corrija estas filas en "${escapar(archivo.name)}", guarde el archivo y vuelva a elegirlo. No se ha cargado ${t.ninguno}.`,
        listaErrores(res.errores))) continue;
      return false;
    }
    if (res.registros.length === 0) {
      if (await problemaConArchivo(`El archivo no tiene ${t.varios}`,
        `"${escapar(archivo.name)}" solo tiene la fila de títulos. ${t.filaVacia}`)) continue;
      return false;
    }

    const decision = await dialogo({
      icono: "bi-clipboard-check",
      paso: "Paso 2 de 2",
      titulo: "Revise antes de cargar",
      texto: "Compare estos datos con su archivo. Si algo no coincide, toque Atrás y elija otro archivo.",
      contenido: revision(archivo.name, res),
      amplio: true,
      confirmar: `Cargar ${t.varios}`,
      iconoConfirmar: "bi-cloud-arrow-up",
      cancelar: "Atrás",
      cerrar: true,
    });
    if (decision.dismiss === Swal.DismissReason.cancel) continue;
    if (!decision.isConfirmed) return false;

    const n = res.registros.length;
    mostrarEspera(`Guardando ${n === 1 ? t.uno : t.varios}`, `Estamos cargando ${cantidad(n)}. No cierre esta página.`);
    const envio = await guardar(res.registros);
    if (!envio.exito) {
      await mostrarError(`No se pudo guardar`, `No se cargó ${t.ninguno}. Intente de nuevo en unos minutos. Detalle: ${escapar(envio.mensaje)}`);
      return false;
    }
    await dialogo({
      icono: "bi-check-circle", tono: "verde",
      titulo: t.exito,
      texto: `Se registraron <strong>${cantidad(n)}</strong>. Ya aparecen en el listado.`,
      confirmar: "Listo",
    });
    return true;
  }
};
